import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { getApiErrorMessage } from './helpers/apiErrorMessage';
import Pagination from './Pagination';

function OtherUserDiary() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [entries, setEntries] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isPrivate, setIsPrivate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async (pageNumber) => {
            setLoading(true);
            setError(null);
            setIsPrivate(false);
            try {
                const userRes = await axios.get(`/api/users/${userId}`);
                setUser(userRes.data);

                try {
                    const diaryRes = await axios.get(`/api/users/${userId}/diary?page=${pageNumber}`);
                    setEntries(diaryRes.data.entries);
                    setPage(diaryRes.data.page);
                    setTotalPages(diaryRes.data.totalPages);
                } catch (err) {
                    if (err.response?.status === 403) {
                        setIsPrivate(true);
                        setEntries([]);
                    } else {
                        throw err;
                    }
                }
            } catch (err) {
                setError(getApiErrorMessage(err, 'load this user diary'));
            } finally {
                setLoading(false);
            }
        };
        load(1);
    }, [userId]);

    const handlePageChange = async (newPage) => {
        if (newPage < 1 || newPage > totalPages || newPage === page) return;
        setLoading(true);
        try {
            setError(null);
            const diaryRes = await axios.get(`/api/users/${userId}/diary?page=${newPage}`);
            setEntries(diaryRes.data.entries);
            setPage(diaryRes.data.page);
            setTotalPages(diaryRes.data.totalPages);
        } catch (err) {
            setError(getApiErrorMessage(err, 'load this user diary'));
        } finally {
            setLoading(false);
        }
    };


    if (loading) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">Loading...</div>;
    }

    if (!user) {
        return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">User not found</div>;
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white px-6 pb-10">
            {error && (
                <div className="bg-red-900 border-l-4 border-red-600 p-4 m-4 rounded">
                    <div className="flex justify-between items-center">
                        <p className="text-red-200">{error}</p>
                        <button onClick={() => setError(null)} className="text-red-400 hover:text-red-200 text-2xl">×</button>
                    </div>
                </div>
            )}
            <div className="max-w-4xl mx-auto">
                <button onClick={() => navigate(`/profile/${userId}`)} className="text-gray-400 hover:text-white mb-6 flex items-center gap-2 transition">
                    <i className="fas fa-arrow-left"></i> Back to profile
                </button>
                <h1 className="text-3xl font-bold mb-6">{user.username}'s Diary</h1>

                {isPrivate ? (
                    <p className="text-gray-400">This user has hidden their diary.</p>
                ) : entries.length === 0 ? (
                    <p className="text-gray-400">No diary entries yet.</p>
                ) : (
                    <div className="space-y-4">
                        {entries.map((entry) => (
                            <div key={entry.entryId} className="bg-gray-800 p-4 rounded-lg flex gap-4 items-center">
                                <img
                                    src={entry.album.albumCover || 'https://placehold.co/75x75'}
                                    alt={entry.album.title}
                                    className="w-14 h-14 rounded-lg object-cover"
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="text-white font-semibold truncate">{entry.album.title}</p>
                                    <p className="text-gray-400 text-sm truncate">{entry.album.artist}</p>
                                    {entry.content && <p className="text-gray-300 text-sm mt-1 line-clamp-2">{entry.content}</p>}
                                </div>
                                <div className="text-right shrink-0">
                                    <p className="text-gray-300 text-sm">{entry.addedOn}</p>
                                    {entry.rating > 0 && <p className="text-green-400 text-sm">Rating: {entry.rating}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={handlePageChange} />
            </div>
        </div>
    );
}

export default OtherUserDiary;
